import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, UserPlus } from 'lucide-react';
import { authApi } from '@/services/api';

const acceptInvitationSchema = z.object({
  password: z.string()
    .min(12, 'Password must be at least 12 characters long')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one lowercase letter, one uppercase letter, one number, and one special character'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type AcceptInvitationFormData = z.infer<typeof acceptInvitationSchema>;

const AcceptInvitationPage = () => {
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AcceptInvitationFormData>({
    resolver: zodResolver(acceptInvitationSchema),
  });

  if (!token) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <div className="fixed inset-0 bg-gradient-to-br from-white via-[#F0F9FF] to-[#E8F4FD]"></div>
        <div className="relative z-10 min-h-screen flex items-center justify-center">
          <div className="w-full max-w-md text-center p-8">
            <div className="mb-4">
              <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Invalid Invitation Link</h2>
            <p className="text-[#69727D] mb-6">
              This invitation link is invalid or has expired. Please contact your administrator for a new invitation.
            </p>
            <button
              onClick={() => navigate('/admin/login')}
              className="md-btn md-btn-primary w-full"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const onSubmit = async (data: AcceptInvitationFormData) => {
    setIsLoading(true);
    setError('');

    try {
      const result = await authApi.acceptInvitation(token, data.password);

      if (result.success) {
        navigate('/admin/login', {
          state: { message: 'Account created successfully! You can now log in with your new password.' }
        });
      } else {
        setError(result.error || 'Failed to accept invitation');
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to accept invitation. The link may have expired.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="fixed inset-0 bg-gradient-to-br from-white via-[#F0F9FF] to-[#E8F4FD]">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#18A5F9]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#18A5F9]/3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center">
        <div className="w-full max-w-md p-8">
          <div className="text-center mb-10">
            <img
              src="/assets/logo.svg"
              alt="Research Clever"
              className="h-14 w-auto mx-auto mb-6"
            />
            <div className="flex items-center justify-center gap-2 mb-2">
              <UserPlus className="h-6 w-6 text-[#18A5F9]" />
              <h1 className="text-2xl font-semibold text-gray-900">Welcome!</h1>
            </div>
            <p className="text-base text-[#69727D]">
              Set your password to complete your account setup
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Create Password
              </label>
              <div className="md-outlined-textfield">
                <input
                  type="password"
                  id="password"
                  {...register('password')}
                  className={`w-full ${errors.password || error ? 'border-red-400' : ''}`}
                  placeholder="Enter password (min 12 chars)"
                  disabled={isLoading}
                />
              </div>
              {errors.password && (
                <p className="text-red-600 text-sm mt-1">{errors.password.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="md-outlined-textfield">
                <input
                  type="password"
                  id="confirmPassword"
                  {...register('confirmPassword')}
                  className={`w-full ${errors.confirmPassword || error ? 'border-red-400' : ''}`}
                  placeholder="Confirm password"
                  disabled={isLoading}
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-red-600 text-sm mt-1">{errors.confirmPassword.message}</p>
              )}
            </div>

            <div className="text-xs text-[#69727D] bg-gray-50 p-3 rounded-lg">
              <p className="font-medium mb-1">Password requirements:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>At least 12 characters</li>
                <li>One uppercase letter</li>
                <li>One lowercase letter</li>
                <li>One number</li>
                <li>One special character (@$!%*?&)</li>
              </ul>
            </div>

            {error && (
              <div className="text-red-700 text-sm text-center bg-red-50 p-3 border border-red-200 rounded-[10px]">{error}</div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="md-btn md-btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Creating account...
                </div>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="text-center mt-6">
            <Link
              to="/admin/login"
              className="text-[#69727D] hover:text-[#4A5259] text-sm transition-colors"
            >
              Already have an account? Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitationPage;
